import { ACCOUNT_PACE_BUCKET_LABELS, type PaceBucket } from '@shared/flow/account-pace';
import { EMPTY_VALUE } from '../account/accountFieldFormat';

/**
 * "60%", "111%", "125%": the warehouse pace ratio as a whole percentage,
 * rounded to the nearest point (0.995 reads "100%"), never "-0%". A
 * non-finite ratio reads as the unknown pace bucket does ("No Pacing").
 */
export function formatPace(ratio: number): string {
  if (!Number.isFinite(ratio)) return ACCOUNT_PACE_BUCKET_LABELS.unknown;
  const percent = Math.round(ratio * 100);
  return `${percent === 0 ? 0 : percent}%`;
}

export function paceLabel(bucket: PaceBucket): string {
  return ACCOUNT_PACE_BUCKET_LABELS[bucket];
}

/** The Salesforce health rating, or "No Rating" while the account has none or a blank one. */
export function healthLabel(health: string | null): string {
  return health?.trim() ? health : 'No Rating';
}

/**
 * "9.4K", "504K", "1.1M", "999", or the empty glyph: a plain count with no
 * currency sign, for the Credit Spent L30 cell. Under ten thousand the
 * thousands keep one decimal ("9.4K"), from ten thousand up they round to a
 * whole number ("61K"). Millions keep one decimal. A trailing ".0" is dropped
 * everywhere. The unit is chosen after rounding, so 999.5 is "1K" and 999,999
 * is "1M". Rounding works on the magnitude, so a negative count rounds away
 * from zero the way its positive twin does ("-1.5K").
 */
export function formatCompactCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return EMPTY_VALUE;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (Math.round(abs / 1e3) >= 1000) return `${sign}${tenths(abs / 1e5)}M`;
  if (Math.round(abs) >= 1000) return `${sign}${abs < 10_000 ? tenths(abs / 100) : Math.round(abs / 1e3)}K`;
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
}

/**
 * `tenthsOfUnit` rounded to a whole number of tenths, then shown with one
 * decimal unless that decimal is 0: 94 reads "9.4", 100 reads "10". Rounding
 * on the integer tenths is what keeps 9,950 at "10K", where `toFixed(1)` on
 * the unit value would round 9.95 down.
 */
function tenths(tenthsOfUnit: number): string {
  const rounded = Math.round(tenthsOfUnit);
  return rounded % 10 === 0 ? String(rounded / 10) : (rounded / 10).toFixed(1);
}

/**
 * A Salesforce multiselect as one line ("Champion left · Budget cut"), or the
 * empty glyph when there are no picks. Flow carries the multiselect as the
 * raw ";" joined text, so this splits it and drops blanks.
 */
export function formatPicks(raw: string | null | undefined): string {
  const picks = (raw ?? '').split(';').map(pick => pick.trim()).filter(pick => pick !== '');
  return picks.length > 0 ? picks.join(' · ') : EMPTY_VALUE;
}

/** A whole count with thousands separators: "2,855". */
export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}
